<template>
  <div>
    <page-header :actions="headerActionConfig" :page="pageHeaderConfig" />
    <board class="mb-4" :details="board" />
    <validation-observer
      ref="practitionerInternRef"
      class="my-5"
      novalidate
      tag="form"
    >
      <div class="row">
        <div class="col-md-3">
          <validation-provider v-slot="{ errors }" rules="required">
            <custom-input
              v-model="model.firstName"
              class="mt-n2"
              custom-error-msg="Favor ingresar el nombre"
              :error="errors[0]"
              label="Nombre:"
              type="text"
            />
          </validation-provider>
        </div>
        <div class="col-md-3 mt-4 mt-md-0 mt-sm-4">
          <validation-provider v-slot="{ errors }" rules="required">
            <custom-input
              v-model="model.lastPaternalName"
              class="mt-n2"
              custom-error-msg="Favor ingresar el apellido paterno"
              :error="errors[0]"
              label="Apellido Paterno:"
              type="text"
            />
          </validation-provider>
        </div>
        <div class="col-md-3 mt-4 mt-md-0 mt-sm-4">
          <custom-input
            v-model="model.lastMaternalName"
            class="mt-n2"
            label="Apellido Materno:"
            type="text"
          />
        </div>
        <div class="col-md-3">
          <action-button
            class="mt-4 ml-0 ml-md-3 ml-sm-0"
            i="fas fa-save"
            text="Guardar Nombre"
            variant="success"
            @click="savePractitionerInfoHandler"
          />
        </div>
      </div>
    </validation-observer>
    <div class="mb-5">
      <specialty-information-list
        v-model="model.specialties"
        :add-enabled="
          userHasPermissions('provider.practitioner.specialty.add') &&
            !isReadOnly
        "
        :delete-enabled="
          userHasPermissions('provider.practitioner.specialty.delete') &&
            !isReadOnly
        "
        :edit-enabled="
          userHasPermissions('provider.practitioner.specialty.edit') &&
            !isReadOnly
        "
        :on-deleting="onSpecialtyDeleting"
        :on-saving="onSpecialtySaving"
      />
      <validation-observer
        ref="documentsRef"
        v-slot="{ handleSubmit }"
        novalidate
        tag="form"
      >
        <documents
          :key="refresherKey"
          :items="model.documents"
          :readonly="isReadOnly"
          @input="showCancelDocumentsEditing = true"
        />
        <div class="row">
          <div class="col">
            <action-button
              class="float-right"
              i="fas fa-save"
              text="Guardar"
              variant="success"
              @click="handleSubmit(saveDocumentsHandler)"
            />
            <action-button
              v-if="showCancelDocumentsEditing"
              class="float-right mr-2"
              i="fas fa-times-circle"
              text="Cancelar"
              variant="danger"
              @click="cancelDocumentsHandler"
            />
          </div>
        </div>
      </validation-observer>
    </div>
    <div>
      <section-title has-line title="Información de Supervisores" />
      <div
        v-if="supervisors.length == 0"
        class="bg-secondary-13 p-4 rounded d-flex justify-content-center align-items-center"
      >
        <span class="color-secondary-6 font-14 font-weight-normal">
          Actualmente no cuenta con un Supervisor asociado
        </span>
      </div>
      <custom-callout
        v-for="(item, index) in supervisors"
        :key="`specialist-info-${index}`"
        class="mt-3"
        :has-toggle="false"
      >
        <div class="mx-3">
          <div class="row">
            <div class="col col-sm col-12">
              <div class="color-secondary-6 font-14">
                Nombre:
              </div>
              <div class="color-black font-16 font-weight-normal">
                {{ item.name }}
              </div>
            </div>
            <div class="col col-sm col-12 mt-3 mt-md-0 mt-sm-3">
              <div class="color-secondary-6 font-14">
                Especialidad:
              </div>
              <div class="color-black font-16 font-weight-normal">
                {{ item.serviceNames }}
              </div>
            </div>
            <div class="col col-sm col-12 mt-3 mt-md-0 mt-sm-3">
              <div class="color-secondary-6 font-14">
                Licencia:
              </div>
              <div class="color-black font-16 font-weight-normal">
                {{ item.licenseNumber }}
              </div>
            </div>
            <div class="col col-sm col-12 mt-3 mt-md-0 mt-sm-3">
              <div class="color-secondary-6 font-14">
                Fecha Expiración:
              </div>
              <div class="color-black font-16 font-weight-normal">
                {{ item.expirationDate | date }}
              </div>
            </div>
          </div>
        </div>
      </custom-callout>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex';

import Board from 'Board';
import SectionTitle from 'SectionTitle';
import CustomCallout from 'custom-callout';
import CustomInput from 'CustomInput';
import ActionButton from 'ActionButton';
import SpecialtyInformationList from './components/SpecialtyInformation/SpecialtyInformationList';
import Documents from './components/Documents/Index';
import alertMixin from 'utils/mixins/alert.mixin';
import {
  getPractitionerDocumentTypes as _getPractitionerDocumentTypes,
  getPractitionerInfo as _getPractitionerInfo,
  updatePractitionerInfo as _updatePractitionerInfo,
  updatePractitionerDocuments as _updatePractitionerDocuments,
  getPractitionerSupervisors as _getPractitionerSupervisors,
  changePractitionerStatus as _changePractitionerStatus,
  createSpecialistSpecialty as _createSpecialistSpecialty,
  editSpecialistSpecialty as _editSpecialistSpecialty,
  deleteSpecialistSpecialty as _deleteSpecialistSpecialty,
} from '@/modules/practitionerIntern/api';
import { getAcademicYear as _getAcademicYear } from '@/modules/shared/academicyear.api';
import documentTypes from '@/utils/shared/constants/documentTypes.js';

export default {
  name: 'PractitionerInternEdit',
  components: {
    Board,
    SectionTitle,
    CustomCallout,
    CustomInput,
    ActionButton,
    SpecialtyInformationList,
    Documents,
  },
  mixins: [alertMixin],
  data: () => ({
    refresherKey: 0,
    showCancelDocumentsEditing: false,
    practitionerId: null,
    companyId: null,
    selectedAcademicYear: null,
    model: {
      firstName: null,
      lastPaternalName: null,
      lastMaternalName: null,
      specialties: [],
      documents: [],
    },
    supervisors: [],
    documentsTypes: [],
    originalsDocuments: [],
  }),
  computed: {
    ...mapState('$_user', ['user']),
    isReadOnly() {
      return !this.userHasPermissions('provider.practitioner.edit');
    },
    pageHeaderConfig() {
      return {
        title: 'Practicantes e Internos',
        icon: 'fas fa-user-tie',
        routes: ['Proveedor', 'Practicantes e Internos'],
      };
    },
    headerActionConfig() {
      return [
        {
          text: 'Regresar',
          icon: 'fas fa-arrow-circle-left',
          variant: 'outline-success',
          click: () => {
            this.$router.go(-1);
          },
        },
        {
          text: this.practitionerStatusInfo.text,
          icon: this.practitionerStatusInfo.icon,
          variant: 'outline-success',
          click: this.changePractitionerStatusHandler,
          visible:
            this.userHasPermissions('provider.practitioner.changestatus') &&
            !this.isReadOnly,
        },
      ];
    },
    board() {
      return [
        [
          {
            label: 'Año Fiscal:',
            icon: 'fas fa-calendar-edit ',
            data: this.selectedAcademicYear,
            cols: 'col col-sm col-12',
          },
          {
            label: 'Nombre:',
            icon: 'fas fa-address-book',
            data: this.model.fullName,
            cols: 'col col-sm col-12',
          },
          {
            label: 'Número Seguro Social:',
            icon: 'fas fa-ballot-check',
            data: this.socialSecurityNumber,
            cols: 'col col-sm col-12',
          },
        ],
      ];
    },
    socialSecurityNumber() {
      if (this.model.socialSecurityNumber)
        return `xxx-xx-${this.model.socialSecurityNumber}`;
      return '';
    },
    practitionerStatusName() {
      return this.model.isActive === true ? 'Inactivar' : 'Re-Activar';
    },
    practitionerStatusConfirmName() {
      return this.model.isActive === true ? 'inactivado' : 'reactivado';
    },
    practitionerStatusInfo() {
      return {
        text: this.practitionerStatusName,
        icon:
          this.model.isActive === true ? 'fas fa-user-slash' : 'fas fa-user',
        message: `Está seguro que desea ${this.practitionerStatusName?.toLowerCase()} el practicante o interno ${
          this.model.fullName
        }?`,
        confirmMessage: `El usuario ha sido ${this.practitionerStatusConfirmName} exitosamente.`,
      };
    },
  },
  async mounted() {
    const { practitionerId, companyId, fiscalYearId } = this.$route.params;
    this.practitionerId = practitionerId;
    this.companyId = companyId;

    await this.callGetPractitionerInfo();
    await this.callGetDocumentTypes();
    await this.callGetSupervisors();

    await _getAcademicYear(fiscalYearId).then(
      (resp) => (this.selectedAcademicYear = resp.data.description)
    );
  },
  methods: {
    async onSpecialtySaving(savedSpecialty) {
      savedSpecialty.practitionerId = parseInt(this.practitionerId);

      let promise = null;

      if (savedSpecialty.isNew)
        promise = _createSpecialistSpecialty(savedSpecialty);
      else promise = _editSpecialistSpecialty(savedSpecialty);

      let result = true;

      await promise
        .then(() => {
          this.ShowToast(
            'Proceso Exitoso',
            'Especialidad guardada exitosamente.',
            'success'
          );

          result = true;
        })
        .catch((error) => {
          this.ShowToast('Error', error.response.data.message, 'error');
          result = false;
        });

      return result;
    },
    async onSpecialtyDeleting(practitionerId, specialtyId) {
      let promise = null;

      promise = _deleteSpecialistSpecialty(practitionerId, specialtyId);

      let result = true;

      await promise
        .then(() => {
          this.ShowToast(
            'Proceso Exitoso',
            'Especialidad eliminada exitosamente.',
            'success'
          );
          result = true;
        })
        .catch((error) => {
          this.ShowToast('Error', error.response.data.message, 'error');
          result = false;
        });

      return result;
    },
    async callGetPractitionerInfo() {
      await _getPractitionerInfo(this.practitionerId, this.companyId).then(
        (resp) => {
          this.model = resp.data;
        }
      );
    },
    async callGetSupervisors() {
      const { data: _result } = await _getPractitionerSupervisors({
        practitionerId: this.practitionerId,
        companyId: this.companyId,
      });
      if (_result) {
        let specialtiesIntern = [];
        const bosses = {};

        this.model.specialties.forEach(
          (s) =>
            (specialtiesIntern = [...specialtiesIntern, s.specialtyType.name])
        );

        _result.forEach((supervisor) => {
          if (!bosses[supervisor.name]) {
            bosses[supervisor.name] = supervisor;
          }
          if (
            supervisor.serviceNames
              .split(',')
              .some((sName) => specialtiesIntern.includes(sName))
          ) {
            bosses[supervisor.name] = supervisor;
          }
        });

        this.supervisors = Object.values(bosses);
      }
    },
    async callGetDocumentTypes() {
      var { data: documentsTypes } = await _getPractitionerDocumentTypes();
      this.documentsTypes = documentsTypes;
      this.mapDocuments();
    },
    async savePractitionerInfoHandler() {
      const isValid = await this.$refs.practitionerInternRef.validate();
      if (!isValid) {
        this.ShowToast('Alerta', 'Información Incompleta', 'error');
        return;
      }
      const { data } = await _updatePractitionerInfo(
        this.practitionerId,
        this.model
      );
      if (data) {
        this.ShowToast(
          'Proceso Exitoso',
          'Los datos han sido guardado exitosamente.',
          'success'
        );
        await this.callGetPractitionerInfo(this.practitionerId);
      }
    },
    async saveDocumentsHandler() {
      var params = new FormData();
      (this.model.documents || []).forEach((document, index) => {
        params.append(`[${index}].file`, document.file);
        params.append(`[${index}].documentTypeId`, document.documentTypeId);
        params.append(`[${index}].itemAttachmentId`, document.itemAttachmentId);
      });
      _updatePractitionerDocuments(this.practitionerId, params)
        .then(({ data }) => {
          this.model.documents = data;
          this.mapDocuments();
          this.showCancelDocumentsEditing = false;
          this.ShowToast(
            'Proceso Exitoso',
            'Documentos guardado exitosamente.',
            'success'
          );
        })
        .catch((error) => {
          this.ShowToast('Error', error.response.data.message, 'error');
        });
    },
    async cancelDocumentsHandler() {
      this.model.documents = JSON.parse(
        JSON.stringify(this.originalsDocuments)
      );
      this.refresherKey++;
      this.showCancelDocumentsEditing = false;
    },
    async changePractitionerStatusHandler() {
      const practitionerId = this.practitionerId;
      const newPractitionerStatus = !this.model.isActive;
      if (practitionerId) {
        const answer = await this.ShowCancelModal(
          'Atención',
          this.practitionerStatusInfo.message,
          'Si',
          'No'
        );
        if (answer) {
          const { data } = await _changePractitionerStatus(
            practitionerId,
            newPractitionerStatus
          );
          if (data) {
            this.ShowToast(
              'Proceso Exitoso',
              this.practitionerStatusInfo.confirmMessage,
              'success'
            );
            this.model.isActive = newPractitionerStatus;
          }
        }
      }
    },
    mapDocuments() {
      const requiredDocTypes = [documentTypes.AgreementWithTheUniversity];
      this.documentsTypes.forEach((docType) => {
        const document = this.model.documents.find(
          (document) => document.documentTypeId == docType.id
        );
        const rules = requiredDocTypes.includes(docType.id) ? 'required' : '';
        if (document) {
          document.itemAttachmentId = document.documentId;
          document.rules = rules;
          return;
        }
        this.model.documents.push({
          documentTypeId: docType.id,
          documentTypeName: docType.name,
          file: null,
          rules,
        });
      });
      this.originalsDocuments = JSON.parse(
        JSON.stringify(this.model.documents)
      );
      this.refresherKey++;
    },
  },
};
</script>
